import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { ssoSignIN } from '../api/signin'
import { msalInstance } from '../configurations/msal-config'
import { useAuth } from '../customHooks/useAuth'
import { useStorage } from '../customHooks/useStorage'
import { User } from '../api/signin'
import { CanBeAnything } from '../types'

export const MSALHandler = () => {
  const { user, setUser, setIsAuthenticated } = useAuth()
  const { setValue: setUserSession } = useStorage<User | null>('user', null, 'session')
  const navigate = useNavigate()

  const signInFn = useCallback(
    async (account: CanBeAnything) => {
      const lionLoginInfo = account
      const idToken = account.idToken
      const username = account.username
      try {
        const signInResponse = await ssoSignIN(username, '', true, idToken)
        const stateObject = {
          ...user,
          email: signInResponse?.data?.data?.email,
          tokens: signInResponse?.data?.data?.tokens,
          name: signInResponse?.data?.data?.name,
          user_type: signInResponse?.data?.data?.user_type,
          businessUser: signInResponse?.data?.data?.businessUser,
          collabUser: signInResponse?.data?.data?.collabUser,
          authenticated: true,
          isSSO: true,
          lionLoginInfo,
          authorisedApp: signInResponse?.data?.data?.authorisedApp,
          roles: signInResponse?.data?.data?.roles,
        }
        setUserSession(stateObject)
        setUser(stateObject)
        setIsAuthenticated(true)
        // On successful login, replace the history to prevent going back to the handler page
        navigate('/overview', { replace: true })
      } catch {
        // If the backend sign-in fails, redirect to the main sign-in page
        navigate('/sign-in', { replace: true })
      }
    },
    [user, setUserSession, setUser, setIsAuthenticated, navigate]
  )

  useEffect(() => {
    const processMsalRedirect = async () => {
      await msalInstance.initialize()

      // This promise resolves with data ONLY on a valid redirect from the auth provider (Microsoft).
      // It will be `null` for history navigation, direct URL access, or a page refresh.
      const response = await msalInstance.handleRedirectPromise()

      if (response && response.account) {
        // SUCCESS: This is a valid redirect from Microsoft. We have an account.
        // Proceed with the sign-in process.
        signInFn({ ...response.account, idToken: response.idToken })
      } else {
        // FAILURE: This is NOT a valid redirect from Microsoft.
        // The user is here via browser history, refresh, or direct navigation.
        // As requested, we redirect them to the sign-in page.
        navigate('/sign-in', { replace: true })
      }
    }

    processMsalRedirect().catch(() => {
      // If any part of the MSAL process fails, it's safest to redirect to sign-in.
      navigate('/sign-in', { replace: true })
    })
    // We only want this effect to run once on component mount.
    // The functions it calls are stable and defined outside the render cycle or have their own memoization.
  }, [])

  return null
}
